import { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Flex, IconButton, Spinner, Text, TextField } from '@radix-ui/themes';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Globe,
  KeyRound,
  LogIn,
  LogOut,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import type { LinkState, WebOp, WebView } from '@mac-remote/protocol';
import type { Connection } from '../lib/connection';
import { getTelegramWebApp } from '../lib/telegram';

/** Recently used sites — addresses only. Logins and passwords are never written to the
 *  phone: localStorage is a file on disk, the same reason the console's sudo field has
 *  its own path around history and drafts. */
const SITES_KEY = 'mac-remote:web-sites';
const SITES_MAX = 6;

function loadSites(): string[] {
  try {
    const raw = localStorage.getItem(SITES_KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((s): s is string => typeof s === 'string').slice(0, SITES_MAX) : [];
  } catch {
    return [];
  }
}

function rememberSite(url: string): string[] {
  const site = url.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
  const next = [site, ...loadSites().filter((s) => s !== site)].slice(0, SITES_MAX);
  try {
    localStorage.setItem(SITES_KEY, JSON.stringify(next));
  } catch {
    // Private mode or full storage: the list is a convenience, not a requirement.
  }
  return next;
}

const KEYS: Array<{ label: string; key: Extract<WebOp, { op: 'key' }>['key'] }> = [
  { label: '⏎', key: 'Enter' },
  { label: 'tab', key: 'Tab' },
  { label: 'esc', key: 'Escape' },
  { label: '⌫', key: 'Backspace' },
];

/** iOS "helps" by capitalising the first letter and swapping -- for a dash; in a login,
 *  an address or a code both are silent breakage. */
const RAW_INPUT = { autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false } as const;

function Verdict({ view }: { view: WebView }) {
  const tone = {
    signed_in: { color: 'var(--jade-11)', icon: <CheckCircle2 size={16} />, text: 'Вошли' },
    rejected: { color: 'var(--red-11)', icon: <XCircle size={16} />, text: 'Не пустило' },
    code: { color: 'var(--amber-11)', icon: <KeyRound size={16} />, text: 'Нужен код' },
    no_form: { color: 'var(--amber-11)', icon: <AlertTriangle size={16} />, text: 'Формы входа нет' },
    error: { color: 'var(--red-11)', icon: <XCircle size={16} />, text: 'Ошибка' },
    page: null,
  }[view.status];
  if (!tone && !view.message) return null;
  return (
    <Flex gap="2" align="start" style={{ color: tone?.color ?? 'var(--app-hint)' }}>
      {tone && <span style={{ flex: '0 0 auto', paddingTop: 2 }}>{tone.icon}</span>}
      <Text size="2">
        {tone && <b>{tone.text}</b>}
        {tone && view.message ? ' — ' : ''}
        {view.message}
      </Text>
    </Flex>
  );
}

/**
 * Logging into sites on the Mac when the Mac's screen is no use: lid shut or brightness
 * at zero, the desktop screenshot is black. This drives a separate headless Chrome on
 * the Mac instead — fill in the address, login and password, and the agent finds the
 * form, types, presses "Войти" and says how it went, with a frame of the page.
 *
 * After that the frame is a pointer, like the Screen tab: a tap is a click on the page,
 * there is a field for typing and keys — for the code, the captcha, whatever the site
 * asks next. "Сначала выйти" is on by default: it wipes the site's cookies first, so an
 * account already signed in there cannot slip through.
 */
export function Browser({
  conn,
  link,
  onConfirm,
}: {
  conn: Connection;
  link: LinkState;
  onConfirm: (q: string, go: () => void) => void;
}) {
  const online = link === 'online';
  const [view, setView] = useState<WebView | null>(null);
  const [pending, setPending] = useState<WebOp['op'] | null>(null);
  const [formOpen, setFormOpen] = useState(true);
  const [url, setUrl] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [fresh, setFresh] = useState(true);
  const [code, setCode] = useState('');
  const [text, setText] = useState('');
  const [sites, setSites] = useState<string[]>(loadSites);

  useEffect(() => {
    const offs = [
      conn.on('web', (v) => {
        setView(v);
        setPending(null);
        if (v.status === 'signed_in') {
          setFormOpen(false);
          getTelegramWebApp()?.HapticFeedback?.notificationOccurred('success');
        } else if (v.status === 'rejected' || v.status === 'error') {
          getTelegramWebApp()?.HapticFeedback?.notificationOccurred('error');
        }
      }),
      // A timeout or an offline Mac arrives as a plain error (shown by the app); all
      // this screen has to do is stop spinning.
      conn.on('error', () => setPending(null)),
    ];
    return () => offs.forEach((off) => off());
  }, [conn]);

  const send = useCallback(
    (op: WebOp) => {
      if (!online || pending) return;
      if (conn.send({ t: 'c.web', op })) setPending(op.op);
    },
    [conn, online, pending],
  );

  const doLogin = () => {
    if (!url.trim() || !password) return;
    send({ op: 'login', url: url.trim(), login: login.trim(), password, fresh });
    // Gone from memory the moment it is on its way — a wrong one is retyped anyway.
    setPassword('');
    setSites(rememberSite(url));
  };

  const doOpen = () => {
    if (!url.trim()) return;
    send({ op: 'open', url: url.trim() });
    setSites(rememberSite(url));
    setFormOpen(false);
  };

  const handleImgClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    send({ op: 'click', x, y });
  };

  const busy = pending !== null;
  const disabled = !online || busy;

  return (
    <Flex direction="column" style={{ height: '100%', minHeight: 0 }}>
      {formOpen ? (
        <Flex direction="column" gap="2" px="3" py="3" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
          <TextField.Root
            placeholder="Сайт, например beget.com"
            value={url}
            inputMode="url"
            {...RAW_INPUT}
            onChange={(e) => setUrl(e.target.value)}
          >
            <TextField.Slot>
              <Globe size={14} />
            </TextField.Slot>
          </TextField.Root>
          {sites.length > 0 && (
            <Flex gap="1" wrap="wrap">
              {sites.map((s) => (
                <Button key={s} size="1" variant={url === s ? 'solid' : 'soft'} color="gray" onClick={() => setUrl(s)}>
                  {s}
                </Button>
              ))}
            </Flex>
          )}
          <TextField.Root
            placeholder="Логин"
            value={login}
            autoComplete="username"
            {...RAW_INPUT}
            onChange={(e) => setLogin(e.target.value)}
          />
          <TextField.Root
            placeholder="Пароль"
            type="password"
            value={password}
            autoComplete="current-password"
            {...RAW_INPUT}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doLogin()}
          />
          <Text as="label" size="2" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <Checkbox checked={fresh} onCheckedChange={(v) => setFresh(v === true)} style={{ marginTop: 2 }} />
            <span>
              Сначала выйти из аккаунта на этом сайте
              <Text as="p" size="1" style={{ color: 'var(--app-hint)', margin: '2px 0 0' }}>
                Сотрёт куки и данные сайта в браузере мака — если там вошли под другим аккаунтом, он не
                помешает
              </Text>
            </span>
          </Text>
          <Flex gap="2">
            <Button style={{ flex: 1 }} color="jade" disabled={disabled || !url.trim() || !password} onClick={doLogin}>
              {pending === 'login' ? <Spinner size="1" /> : <LogIn size={14} />}
              {pending === 'login' ? 'Вхожу…' : 'Войти'}
            </Button>
            <Button variant="soft" color="gray" disabled={disabled || !url.trim()} onClick={doOpen}>
              Просто открыть
            </Button>
            {view && (
              <Button variant="ghost" color="gray" onClick={() => setFormOpen(false)}>
                Скрыть
              </Button>
            )}
          </Flex>
          {!view && (
            <Text size="1" style={{ color: 'var(--app-hint)' }}>
              Отдельный Chrome на маке без окна: работает с закрытой крышкой и тёмным экраном. Логин и
              пароль на телефоне не сохраняются.
            </Text>
          )}
        </Flex>
      ) : (
        <Flex align="center" gap="2" px="3" py="2" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
          <Button size="1" variant="soft" color="jade" onClick={() => setFormOpen(true)}>
            <LogIn size={13} /> Вход
          </Button>
          <IconButton size="1" variant="soft" color="gray" disabled={disabled} onClick={() => send({ op: 'back' })}>
            <ArrowLeft size={14} />
          </IconButton>
          <IconButton size="1" variant="soft" color="gray" disabled={disabled} onClick={() => send({ op: 'shot' })}>
            {busy ? <Spinner size="1" /> : <RefreshCw size={13} />}
          </IconButton>
          <IconButton size="1" variant="soft" color="gray" disabled={disabled} onClick={() => send({ op: 'scroll', dir: 'up' })}>
            <ChevronUp size={14} />
          </IconButton>
          <IconButton size="1" variant="soft" color="gray" disabled={disabled} onClick={() => send({ op: 'scroll', dir: 'down' })}>
            <ChevronDown size={14} />
          </IconButton>
          <Button
            size="1"
            variant="soft"
            color="red"
            disabled={disabled || !view?.url.startsWith('http')}
            style={{ marginLeft: 'auto' }}
            onClick={() =>
              onConfirm('Выйти с этого сайта? Куки и данные сайта в браузере мака будут удалены.', () =>
                send({ op: 'logout' }),
              )
            }
          >
            <LogOut size={13} /> Выйти
          </Button>
        </Flex>
      )}

      {view && (
        <Flex direction="column" gap="2" px="3" py="2" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
          <Verdict view={view} />
          {view.status === 'code' && (
            <Flex gap="2">
              <TextField.Root
                style={{ flex: 1 }}
                placeholder="Код из СМС или приложения"
                value={code}
                inputMode="numeric"
                autoComplete="one-time-code"
                {...RAW_INPUT}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && code.trim()) {
                    send({ op: 'code', code: code.trim() });
                    setCode('');
                  }
                }}
              />
              <Button
                color="amber"
                disabled={disabled || !code.trim()}
                onClick={() => {
                  send({ op: 'code', code: code.trim() });
                  setCode('');
                }}
              >
                {pending === 'code' ? <Spinner size="1" /> : null}
                Отправить
              </Button>
            </Flex>
          )}
          <Text size="1" style={{ color: 'var(--app-hint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {view.title ? `${view.title} · ` : ''}
            {view.url}
          </Text>
          <Flex gap="2">
            <TextField.Root
              style={{ flex: 1 }}
              size="1"
              placeholder="Печатать на странице…"
              value={text}
              {...RAW_INPUT}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && text) {
                  send({ op: 'type', text });
                  setText('');
                }
              }}
            />
            {KEYS.map((k) => (
              <Button key={k.key} size="1" variant="soft" color="gray" disabled={disabled} onClick={() => send({ op: 'key', key: k.key })}>
                {k.label}
              </Button>
            ))}
          </Flex>
        </Flex>
      )}

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--gray-2)', position: 'relative' }}>
        {view?.shot ? (
          <img
            src={`data:image/jpeg;base64,${view.shot.b64}`}
            alt="Страница в браузере мака"
            onClick={disabled ? undefined : handleImgClick}
            style={{ display: 'block', width: '100%', height: 'auto', cursor: 'crosshair', opacity: busy ? 0.55 : 1 }}
          />
        ) : busy ? (
          <Flex align="center" justify="center" gap="2" style={{ padding: 32, color: 'var(--app-hint)' }}>
            <Spinner /> <Text size="2">Открываю страницу на маке…</Text>
          </Flex>
        ) : !online ? (
          <Text as="p" size="2" align="center" style={{ padding: 32, color: 'var(--app-hint)' }}>
            Мак не на связи
          </Text>
        ) : null}
      </div>
    </Flex>
  );
}
